// Pre-bid margin & profit — ONE formula, used by the Pre-Bid Calculator page,
// the Pre-Bid tab on Add/Update Record, the Job Tracker and the Cashflow report.
//
// Cost of the job = labor & material (per square / foot / window / door)
//                 + marketing fee (2.7% of the sale price)
//                 + rep commission, exactly as the Commission Calculator works it out:
//                   rate × (price per unit − floor) × quantity  (never below $0)
// Profit = sale price − cost.  Margin = profit ÷ sale price.  Minimum margin = 40%.
// Every commission floor is read from the Commission Calculator (TRADES), so a floor
// changed there is picked up here automatically.
import { TRADES } from '../components/TradeCalculator';

// ── Numbers to change here ──────────────────────────────────
export const MARKETING_FEE = 0.027;   // 2.7% of the sale price
export const MIN_MARGIN = 0.40;       // at least 40%
export const TARGET_MARGIN = 0.55;    // the goal: what the job should sell for to make 55% (same cost as the bid)
export const DEFAULT_RATE = 0.40;     // commission rate when none is picked

// One entry per thing we sell. `cost` = labor + material per unit.
// `floor` = the Commission Calculator's floor for it (looked up, never typed twice).
const variantFloor = (trade, label) => (TRADES[trade].variants || []).find(v => v.label === label)?.floor;
export const PREBID_TRADES = [
  { key: 'coating',       label: 'Coating',                    cost: 300,  unit: 'square', units: 'squares', floor: () => TRADES.coating.floor },
  { key: 'coating_walls', label: 'Coating — walls only',       cost: 250,  unit: 'square', units: 'squares', floor: () => variantFloor('coating', 'Walls only') },
  { key: 'painting',      label: 'Paint',                      cost: 200,  unit: 'square', units: 'squares', floor: () => TRADES.painting.floor },
  { key: 'gutters',       label: 'Gutters',                    cost: 7.5,  unit: 'foot',   units: 'feet',    floor: () => TRADES.gutters.floor },
  { key: 'windows',       label: 'Windows',                    cost: 400,  unit: 'window', units: 'windows', floor: () => TRADES.windows.floor },
  { key: 'patio_small',   label: 'Patio door 6–8 ft',          cost: 1500, unit: 'door',   units: 'doors',   floor: () => TRADES.windows.patio[0].floor },
  { key: 'patio_large',   label: 'Patio door 10–12 ft',        cost: 2500, unit: 'door',   units: 'doors',   floor: () => TRADES.windows.patio[1].floor },
];
// Hand-typed options for the record's Pre-Bid tab (same as the Pre-Bid (Roofing) and
// Pre-Bid (Manual) pages): labor, materials, commission and other costs are all typed in.
export const MANUAL_TRADES = [
  { key: 'roofing_manual', label: 'Roofing (type costs)',          manual: true, unit: 'square', units: 'squares', needsQty: true },
  { key: 'manual',         label: 'Manual (no measurements)',      manual: true, unit: 'job',    units: 'jobs',    needsQty: false },
];
// ────────────────────────────────────────────────────────────

export const tradeInfo = key => PREBID_TRADES.find(t => t.key === key) || MANUAL_TRADES.find(t => t.key === key) || null;
export const isManualTrade = key => !!tradeInfo(key)?.manual;

const num = v => { const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, '')); return Number.isFinite(n) && n > 0 ? n : 0; };

// The job's sale price: the Change Order Amount is the job's NEW TOTAL when there is one,
// otherwise the Contract Amount (same rule the Job Tracker uses).
export function jobSalePrice(job) {
  return num(job?.change_order_amt) || num(job?.contract_amt);
}

// Smallest sale price that leaves the given margin (MIN_MARGIN unless another is passed)
export function minimumSale(laborMaterial, floorTotal, rate, margin = MIN_MARGIN) {
  const keep = 1 - MARKETING_FEE - margin;
  const noCommission = laborMaterial / keep;           // priced at/below the floor → no commission
  if (noCommission <= floorTotal) return noCommission;
  if (keep - rate <= 0) return null;
  return (laborMaterial - rate * floorTotal) / (keep - rate);
}

// Full breakdown, or null if trade / quantity / sale price are missing.
// (`squares` is the quantity — squares, feet, windows or doors depending on the trade.)
export function computePrebid({ trade, squares, sale, rate, labor, materials, other, commission: commissionTyped }) {
  const t = tradeInfo(trade);
  const qty = num(squares), price = num(sale);
  if (t?.manual) return computeManual({ t, qty, price, labor, materials, other, commission: commissionTyped });
  const typed = v => String(v ?? '').trim() !== '';
  // Admin override: the job's own labor + materials + other (and commission, if typed) replace the standard cost
  const override = [labor, materials, other, commissionTyped].some(typed);
  // Override with no trade or no quantity: still work out the margin from the typed costs alone
  if (override && (!t || !qty)) return computeManual({ t: t || CUSTOM, qty: 0, price, labor, materials, other, commission: commissionTyped, override: true });
  const r = num(rate) || DEFAULT_RATE;
  const floor = t ? Number(t.floor()) : NaN;
  if (!t || !Number.isFinite(floor) || !qty || !price) return null;
  const laborMaterial = override ? num(labor) + num(materials) + num(other) : qty * t.cost;
  const marketing = price * MARKETING_FEE;
  const perUnit = price / qty;
  const floorTotal = floor * qty;
  const commissionEntered = override && typed(commissionTyped);
  const commission = commissionEntered ? num(commissionTyped) : r * Math.max(price - floorTotal, 0);
  const totalCost = laborMaterial + marketing + commission;
  const profit = price - totalCost;
  const margin = profit / price;
  return {
    trade, tradeLabel: t.label, unit: t.unit, units: t.units,
    override, labor: num(labor), materials: num(materials), other: num(other),
    squares: qty, sale: price, rate: r, floor, costPerSq: t.cost, perSquare: perUnit,
    laborMaterial, marketing, commission, commissionEntered, totalCost, profit, margin,
    ok: margin >= MIN_MARGIN, belowFloor: !commissionEntered && perUnit < floor,
    minSale: commissionEntered ? totalCost / (1 - MIN_MARGIN) : minimumSale(laborMaterial, floorTotal, r),
    // Price for the target margin with the SAME cost as this bid: cost ÷ (1 − 55%)
    targetSale: totalCost / (1 - TARGET_MARGIN),
    targetOk: margin >= TARGET_MARGIN,
  };
}

// Roofing (type costs) / Manual: every cost is typed; only the 2.7% marketing fee is added.
// Price for 40% / 55% keep the same costs: total cost ÷ (1 − margin).
const CUSTOM = { key: '', label: 'Cost override', unit: 'job', units: 'jobs', needsQty: false };
function computeManual({ t, qty, price, labor, materials, other, commission, override = false }) {
  const L = num(labor), M = num(materials), O = num(other), C = num(commission);
  if (!price || !(L + M + O + C)) return null;
  if (t.needsQty && !qty) return null;
  qty = t.needsQty ? qty : 0;
  const laborMaterial = L + M + O;
  const marketing = price * MARKETING_FEE;
  const totalCost = laborMaterial + C + marketing;
  const profit = price - totalCost;
  const margin = profit / price;
  return {
    trade: t.key, tradeLabel: t.label, unit: t.unit, units: t.units, manual: true, override,
    labor: L, materials: M, other: O, squares: qty, sale: price,
    rate: 0, floor: 0, perSquare: qty ? price / qty : 0,
    laborMaterial, marketing, commission: C, totalCost, profit, margin,
    ok: margin >= MIN_MARGIN, belowFloor: false,
    minSale: totalCost / (1 - MIN_MARGIN),
    targetSale: totalCost / (1 - TARGET_MARGIN),
    targetOk: margin >= TARGET_MARGIN,
  };
}

// For a saved job record. Sale price = the Pre-Bid tab's own sale price if one was typed
// (e.g. the coating part of a bigger job), otherwise the job's sale price.
export function prebidForJob(job) {
  if (!job) return null;
  const hasCosts = [job.prebid_labor, job.prebid_materials, job.prebid_other, job.prebid_commission].some(v => v !== null && v !== undefined && String(v).trim() !== '');
  if (!job.prebid_trade && !hasCosts) return null;
  return computePrebid({
    trade: job.prebid_trade,
    squares: job.prebid_squares,
    sale: num(job.prebid_sale) || jobSalePrice(job),
    rate: job.prebid_rate,
    labor: job.prebid_labor, materials: job.prebid_materials, other: job.prebid_other,
    commission: job.prebid_commission,
  });
}

// Totals across many jobs: total profit, and the overall margin on the jobs that have pre-bid info.
export function prebidTotals(jobs) {
  let sale = 0, profit = 0, count = 0;
  for (const j of jobs) {
    const p = prebidForJob(j);
    if (!p) continue;
    sale += p.sale; profit += p.profit; count += 1;
  }
  return { count, sale, profit, margin: sale ? profit / sale : null };
}
